import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationPublicId, UUIDString } from "../../../../common/service/server/Types";
import { picSorter } from "../../../util/sort";
import { DescriptionCheckbox } from "../../input/DescriptionCheckbox";
import { QuestionLabel } from "../survey/QuestionContainer";

type CollectionCheckboxCalloutProps = {
  label?: string | ReactNode;
  helpText?: ReactNode;
  categories: CategoryDto[];
  orgId: OrganizationPublicId;
  group: CollectionGroupDetailsDto;
  collected: UUIDString[];
  pending: UUIDString[];
  byGroup?: boolean;
  allCheckbox?: boolean;
  disabledCategoryIds?: UUIDString[];
  containerClassName?: string;
  onChange?: (collectedIds: string[]) => void;
  disabledTooltipFn?: (checked: boolean) => string;
};

export const CollectionCheckboxCallout: React.FC<CollectionCheckboxCalloutProps> = ({
  label,
  helpText,
  categories,
  orgId,
  group,
  collected,
  pending,
  byGroup = true,
  allCheckbox = false,
  disabledCategoryIds = [],
  containerClassName = "my-xl",
  disabledTooltipFn,
  onChange,
}) => {
  const {
    mutators: { patch },
  } = useDataMap(orgId);

  const sorted = useMemo(() => categories?.sort(picSorter) ?? [], [categories]);

  const allChecked = useMemo(
    () => categories.every((c) => collected.includes(c.id)),
    [categories, collected],
  );

  const handleChecked = useCallback(
    (id: UUIDString, isCollected: boolean) => {
      patch.mutate({
        operations: [
          {
            action: isCollected ? "ADD" : "REMOVE",
            target: "COLLECTION",
            picId: id,
            groupId: group.id,
          },
        ],
      });
    },
    [group, patch],
  );

  const handleAllChecked = (all: boolean) => {
    patch.mutate({
      operations: categories.map((c) => ({
        action: all ? "ADD" : "REMOVE",
        target: "COLLECTION",
        groupId: group.id,
        picId: c.id,
      })),
    });
  };

  useEffect(() => {
    if (onChange) {
      onChange(collected);
    }
    // onChange is not usually memoized and causes a runaway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collected]);

  let picGroupDisplays = [{ label: undefined, categories: sorted }];

  if (byGroup) {
    const picGroups = [...new Set(categories.map((c) => c.picGroupId))];
    picGroupDisplays = picGroups.map((groupId) => {
      const matching = categories.filter((c) => c.picGroupId == groupId);
      return {
        label: matching[0].picGroupName,
        categories: matching,
      };
    });
  }

  return (
    <>
      {Boolean(label) && <QuestionLabel label={label} helpText={helpText} />}

      <div className={containerClassName}>
        {allCheckbox && (
          <DescriptionCheckbox
            className="mb-lg"
            label="Select All"
            checked={allChecked}
            onChange={(e, checked) => handleAllChecked(checked)}
          />
        )}

        {picGroupDisplays.map((picGroup, idx) => {
          return (
            <CollectionCheckboxGroup
              key={idx}
              {...picGroup}
              collectedIds={collected}
              pendingIds={pending}
              onChecked={handleChecked}
              disabledCategoryIds={disabledCategoryIds}
              disabledTooltipFn={disabledTooltipFn}
            />
          );
        })}
      </div>
    </>
  );
};
type CollectionCheckboxesGroupProps = {
  categories: CategoryDto[];
  collectedIds: string[];
  pendingIds: string[];
  label: string | undefined;
  onChecked: (id: string, isCollected: boolean) => void;
  disabledCategoryIds?: string[];
  disabledTooltipFn?: (checked: boolean) => string;
};
const CollectionCheckboxGroup: React.FC<CollectionCheckboxesGroupProps> = ({
  categories,
  collectedIds,
  pendingIds,
  label,
  onChecked,
  disabledCategoryIds,
  disabledTooltipFn,
}) => {
  return (
    <>
      {Boolean(label) && <h5>{label}</h5>}
      <PICCheckboxes
        categories={categories}
        checked={collectedIds}
        onChecked={onChecked}
        disabledCategoryIds={disabledCategoryIds}
        disabledTooltipFn={disabledTooltipFn}
        pendingIds={pendingIds}
      />
    </>
  );
};

type PICCheckboxesProps = {
  categories: CategoryDto[];
  checked: string[];
  onChecked: (id: string, isCollected: boolean) => void;
  descriptions?: boolean;
  disabledCategoryIds?: string[];
  disabledTooltipFn?: (checked: boolean) => string;
  pendingIds: string[];
};
export const PICCheckboxes: React.FC<PICCheckboxesProps> = ({
  categories,
  checked,
  onChecked,
  descriptions = true,
  disabledCategoryIds = [],
  disabledTooltipFn,
  pendingIds,
}) => {
  const render = (cat: CategoryDto) => {
    let disabledTooltip: string | undefined;
    if (disabledTooltipFn) {
      disabledTooltip = disabledTooltipFn(checked.includes(cat.id));
    }
    return (
      <DescriptionCheckbox
        key={cat.id}
        label={cat.name}
        description={descriptions && cat.description}
        checked={checked.includes(cat.id)}
        loading={pendingIds.includes(cat.id)}
        onChange={(e, checked) => {
          onChecked(cat.id, checked);
        }}
        disabled={Array.isArray(disabledCategoryIds) && disabledCategoryIds.includes(cat.id)}
        disabledTooltip={disabledTooltip}
        color="primary"
      />
    );
  };

  if (categories.length < 2) {
    return <>{categories.map(render)}</>;
  }

  const left = categories.filter((_, idx) => idx % 2 == 0);
  const right = categories.filter((_, idx) => idx % 2 == 1);

  return (
    <>
      <div className="flex-row">
        <div className="flex-column w-50">{left.map(render)}</div>
        <div className="flex-column w-50">{right.map(render)}</div>
      </div>
    </>
  );
};
